import type { Brief, BriefCard } from "../../shared/contracts";
import { capitalize, fmtDay, safeHttpsUrl } from "../lib/format";
import { IconExternal } from "./icons";
import { Badge, cx } from "./ui";

const CATEGORY_TONE: Record<string, "accent" | "warning" | "success" | "neutral" | "danger" | "info"> = {
  agenda: "accent",
  emails: "warning",
  email: "warning",
  missions: "success",
  mission: "success",
  rappel: "warning",
  actu: "info",
  meteo: "info",
  météo: "info",
};

/** How a category the agent wrote (« meteo », « emails »…) reads on a badge; others are capitalised as written. */
const CATEGORY_LABEL: Record<string, string> = {
  agenda: "Agenda",
  emails: "E-mails",
  email: "E-mail",
  mails: "E-mails",
  missions: "Missions",
  mission: "Mission",
  rappel: "Rappel",
  rappels: "Rappels",
  actu: "Actu",
  meteo: "Météo",
  météo: "Météo",
  suivi: "Suivi",
  info: "Info",
  validations: "Validations",
  anniversaire: "Anniversaire",
  ferie: "Férié",
  férié: "Férié",
};

function rank(card: BriefCard): number {
  return card.priority === "high" ? 0 : 1;
}

/** One brief: headline as a heading, intro, then its cards as a list (data-testid="brief-card"). */
export function BriefView({ brief, headingLevel = 3 }: { brief: Brief; headingLevel?: 2 | 3 }) {
  const cards = [...(brief.cards ?? [])].sort((a, b) => rank(a) - rank(b));
  const Heading = headingLevel === 2 ? "h2" : "h3";
  return (
    <div className="animate-rise min-w-0">
      <p className="text-[13px] font-medium text-muted">
        {brief.kind === "evening" ? "Récap du soir" : "Brief du matin"} · {fmtDay(brief.createdAt)}
      </p>
      <Heading className="mt-1 font-display text-[22px] leading-snug font-medium tracking-[-0.01em] wrap-anywhere sm:text-[24px]">{brief.headline}</Heading>
      {brief.intro ? <p className="mt-2 max-w-3xl text-[15.5px] leading-[1.55] whitespace-pre-line text-body wrap-anywhere sm:text-[16px]">{brief.intro}</p> : null}
      {cards.length ? (
        <ul className="mt-4 flex flex-col">
          {cards.map((card, i) => (
            <BriefCardView key={`${i}-${card.title}`} card={card} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function BriefCardView({ card }: { card: BriefCard }) {
  const url = safeHttpsUrl(card.url);
  const category = card.category?.trim() || "info";
  const high = card.priority === "high";
  return (
    <li data-testid="brief-card" className="flex min-w-0 gap-3.5 border-t border-sunken py-3.5 last:pb-0">
      <span
        aria-hidden="true"
        className={cx("flex h-9 w-9 shrink-0 items-center justify-center rounded-[11px] font-display text-[17px] leading-none", high ? "bg-accent-soft text-accent" : "bg-inset text-fg")}
      >
        {(CATEGORY_LABEL[category.toLowerCase()] ?? category).charAt(0).toLocaleUpperCase("fr")}
      </span>
      <div className="min-w-0 flex-1">
        <h4 className="text-[15px] leading-snug font-semibold wrap-anywhere">
          {card.title}
          {high ? <span className="ml-1.5 text-[12px] font-semibold text-accent">Prioritaire</span> : null}
        </h4>
        {card.body ? <p className="mt-0.5 text-[14px] leading-relaxed whitespace-pre-line text-muted wrap-anywhere">{card.body}</p> : null}
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
          <Badge tone={CATEGORY_TONE[category.toLowerCase()] ?? "neutral"}>{CATEGORY_LABEL[category.toLowerCase()] ?? capitalize(category)}</Badge>
          {url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer nofollow"
              referrerPolicy="no-referrer"
              className="inline-flex min-h-11 items-center gap-1 text-[13.5px] font-semibold text-accent hover:text-accent-hover sm:min-h-0"
            >
              Ouvrir <span className="font-normal text-muted">· {new URL(url).hostname.replace(/^www\./, "")}</span>
              <IconExternal width={13} height={13} />
              <span className="sr-only">(nouvel onglet)</span>
            </a>
          ) : null}
        </div>
      </div>
    </li>
  );
}
